import React from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import { DropDownList, MultiSelect } from '@progress/kendo-react-dropdowns';
import { DatePicker, DateTimePicker } from '@progress/kendo-react-dateinputs';
import { ContentTypeFieldResponse, CPS_CONTENT_TYPE_FIELD_TYPE } from '@cs-cms/types';
import { getFileItemKey } from '../fieldInteractions/FieldInteractionsHelper';
import { FieldLoadingWrapper } from '../FieldLoadingWrapper';

interface CustomFieldCellProps extends GridCellProps {
  ctField: ContentTypeFieldResponse;
  getPerFileFieldChoices: (fileKey: string, field: ContentTypeFieldResponse) => string[];
  isPerFileFieldEnabled: (fileKey: string, field: ContentTypeFieldResponse) => boolean;
  isPerFileFieldLoading: (fileKey: string, field: ContentTypeFieldResponse) => boolean;
}

export const CustomFieldCell: React.FC<CustomFieldCellProps> = props => {
  const { ctField, getPerFileFieldChoices, isPerFileFieldEnabled, isPerFileFieldLoading, dataItem, field, onChange, dataIndex } =
    props;

  const fieldName = field?.replace('perFileMetadata.', '') || '';
  const value = dataItem?.perFileMetadata?.[fieldName];
  const fileKey = getFileItemKey(dataItem);
  const isEnabled = isPerFileFieldEnabled(fileKey, ctField);
  const isLoading = isPerFileFieldLoading(fileKey, ctField);
  const isDisabled = ctField.readOnly || isLoading || !isEnabled;

  const handleChange = (newValue: unknown) => {
    if (onChange) {
      onChange({
        dataItem,
        dataIndex,
        field,
        syntheticEvent: {} as React.SyntheticEvent,
        value: newValue
      });
    }
  };

  const wrapWithLoading = (fieldControl: React.ReactNode): React.ReactNode => (
    <FieldLoadingWrapper isLoading={isLoading}>{fieldControl}</FieldLoadingWrapper>
  );

  switch (ctField.type) {
    case CPS_CONTENT_TYPE_FIELD_TYPE.DATE:
      return wrapWithLoading(
        <DatePicker
          value={value}
          onChange={e => handleChange(e.value)}
          required={ctField.required}
          disabled={isDisabled}
        />
      );

    case CPS_CONTENT_TYPE_FIELD_TYPE.DATETIME:
      return wrapWithLoading(
        <DateTimePicker
          value={value}
          onChange={e => handleChange(e.value)}
          required={ctField.required}
          disabled={isDisabled}
        />
      );

    case CPS_CONTENT_TYPE_FIELD_TYPE.BOOL:
      return wrapWithLoading(
        <Checkbox
          value={value || false}
          onChange={e => handleChange(e.value)}
          required={ctField.required}
          disabled={isDisabled}
        />
      );

    case CPS_CONTENT_TYPE_FIELD_TYPE.CHOICE: {
      const choices = getPerFileFieldChoices(fileKey, ctField);
      return wrapWithLoading(
        <DropDownList
          data={choices}
          value={value || null}
          onChange={e => handleChange(e.value)}
          required={ctField.required}
          disabled={isDisabled}
        />
      );
    }
    case CPS_CONTENT_TYPE_FIELD_TYPE.MULTI_CHOICE: {
      const multiChoices = getPerFileFieldChoices(fileKey, ctField);
      return wrapWithLoading(
        <MultiSelect
          data={multiChoices}
          value={Array.isArray(value) ? value : []}
          onChange={e => handleChange(e.value)}
          required={ctField.required}
          disabled={isDisabled}
        />
      );
    }
    default:
      return <span>Unsupported field type: {ctField.type}</span>;
  }
};
